import { convertFromRaw, convertToRaw, EditorState } from "draft-js";
import draftToHtml from "draftjs-to-html";
import moment from "moment";
import { confirmAlert } from "react-confirm-alert";
import { AdsEntityTypeEnum } from "../controller/classified-ads-ctr/detail-ads-ctr/type";
import timeZone from "./timezones.json";
import * as momentTimezone from 'moment-timezone';

import host, {
  baseUrlDrupal,
  CONTRIBUTOR,
  GUEST,
  HIDDEN_INFORMATION,
  INVESTOR,
  ISV,
  MODERATOR,
  OEM_PROGRAM,
  RESELLER,
} from "../service/constant";
import { contact } from "./country_phone";

export const _isArray = (value) => {
  return (
    value !== null &&
    Object.prototype.toString.call(value) === "[object Array]" &&
    value.length >= 0
  );
};

const regex = (expression) => (string) => {
  let reg = new RegExp(expression);
  return reg.test(string);
};

export const _isDate = (value) =>
  Object.prototype.toString.call(value) === "[object Date]";

export const isNil = (value) => value === null || value === undefined;

export const _isNumber = (value) =>
  Object.prototype.toString.call(value) === "[object Number]";

export const _isObject = (value) =>
  Object.prototype.toString.call(value) === "[object Object]";

export const _isString = (value) =>
  Object.prototype.toString.call(value) === "[object String]";

/*
 * Checks for a 'Number'
 * @param number
 * @return boolean
 */
export const isNumber = (number) => _isNumber(number);

/*
 * Validates that 'number' is less than or equal to (<=) the value
 * @param maxValue
 * @return boolean
 */
export const maxNumber = (maxValue = Number.MAX_VALUE, value) =>
  _isNumber(value) && value <= maxValue;

/*
 * Validates that 'number' is greater than or equal to (>=) the value
 * @param minValue
 * @return boolean
 */
export const minNumber = (minValue = Number.MIN_VALUE, value) =>
  _isNumber(value) && value >= minValue;

/*
 * Validates if number is within the range
 * @param minValue
 * @param maxValue
 * @return boolean
 */
export const between = (
  minValue = Number.MIN_VALUE,
  maxValue = Number.MAX_VALUE,
  value
) => {
  return minLength(minValue, value) && maxLength(maxValue, value);
};

export const isOnlyNumber = (txt) => {
  var pattern = /^-?[0-9]+$/;
  return pattern.test(txt);
};

export const checkAndSetIfIsNumber = (txt, fn) => {
  if (isOnlyNumber(txt) || txt == "") fn(txt);
};

/*
 * Checks for a 'String'
 * @param string - value for check
 * @return boolean
 */
export const isString = (string) => _isString(string);

/*
 * Validates the size of the 'string' is greater than or equal to (> =) the max
 * @param minLength
 * @param string - value for check
 * @return boolean
 */
export const minLength = (min = Number.MIN_VALUE, value) => {
  if (isNil(min)) {
    min = Number.MIN_VALUE;
  }
  return _isString(value) && value.length >= min;
};

/*
 * Validates the size of the 'string' is less than or equal to (<=) the min
 * @param maxLength
 * @param string - value for check
 * @return boolean
 */
export const maxLength = (max = Number.MAX_VALUE, value) => {
  if (isNil(max)) {
    max = Number.MAX_VALUE;
  }
  return _isString(value) && value.length <= max;
};

/*
 * Validates that the length of the string is within the range
 * @param min
 * @param max
 * @return boolean
 */
export const length = (min = Number.MIN_VALUE, max = Number.MAX_VALUE, value) =>
  minLength(min, value) && maxLength(max, value);

/*
 * Checks for data
 * @params value
 * @return boolean
 */
export const isRequired = (value) => {
  if (_isArray(value)) {
    return value.length > 0;
  }
  if (_isNumber(value)) {
    return value > 0;
  }
  if (_isObject(value)) {
    return Object.keys(value).length > 0;
  }
  if (_isString(value)) {
    return value && value.length > 0;
  }
  return Boolean(value);
};

export const isNotNull = (txt) => txt && _isString(txt);

export const isHaveCompanyLogo = (value) => {
  const splitName = value?.split("/");
  const currentLogoName = splitName?.[splitName.length - 1];
  return currentLogoName?.toString() !== "defaultCompany.png";
};

export const validateCompleteInfo = (company) => {
  const {
    company_legal_name,
    company_name,
    website,
    annual_turnover,
    founding_date,
    company_size,
    team_differentiator_pitch,
  } = company;

  const hQlocation = company?.country?.id.toString();
  return (
    isRequired(company_legal_name) &&
    isRequired(company_name) &&
    isRequired(website) &&
    isRequired(annual_turnover) &&
    isRequired(founding_date) &&
    isRequired(company_size) &&
    isRequired(hQlocation) &&
    isRequired(team_differentiator_pitch)
  );
};

/*
 * Checks for email
 * @params email
 * @return boolean
 */
export const isEmail = (email) => {
  const sQtext = "[^\\x0d\\x22\\x5c\\x80-\\xff]";
  const sDtext = "[^\\x0d\\x5b-\\x5d\\x80-\\xff]";
  const sAtom =
    "[^\\x00-\\x20\\x22\\x28\\x29\\x2c\\x2e\\x3a-\\x3c\\x3e\\x40\\x5b-\\x5d\\x7f-\\xff]+";
  const sQuotedPair = "\\x5c[\\x00-\\x7f]";
  const sDomainLiteral = "\\x5b(" + sDtext + "|" + sQuotedPair + ")*\\x5d";
  const sQuotedString = "\\x22(" + sQtext + "|" + sQuotedPair + ")*\\x22";
  const sDomainRef = sAtom;
  const sSubDomain = "(" + sDomainRef + "|" + sDomainLiteral + ")";
  const sWord = "(" + sAtom + "|" + sQuotedString + ")";
  const sDomain = sSubDomain + "(\\x2e" + sSubDomain + ")*";
  const sLocalPart = sWord + "(\\x2e" + sWord + ")*";
  const sAddrSpec = sLocalPart + "\\x40" + sDomain;
  const sValidEmail = "^" + sAddrSpec + "$";
  return regex(sValidEmail)(email);
};

/**
 * Check for URL
 *
 * Copyright (c) 2010-2013 Diego Perini (http://www.iport.it), MIT licensed
 * https://gist.github.com/dperini/729294
 * see also https://mathiasbynens.be/demo/url-regex
 *
 * @params url
 * @return boolean
 */
export const isURL = (url) => {
  const regexValue =
    /(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)/g;
  return regex(regexValue)(url);
};

export const validConstructorUrl = (url) => {
  const regexValue =
    /(http(s)?:\/\/.)[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)/g;
  return regex(regexValue)(url);
};

export const setSessionStorageObject = (obj) => {
  if (isNil(obj)) {
    return;
  }
  if (_isObject(obj)) {
    for (const key in obj) {
      sessionStorage.setItem(key, obj[key]);
    }
  }
};

export const validateNumber = (e) => {
  let pattern = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "+", "0"];
  return pattern.includes(e);
};

export const showDynamicTextDrupal = (data, rulles, key) => {
  let value = data?.find(
    (element) => element["field_key"]?.toString() === rulles?.toString()
  );
  if (value) {
    return value[key];
  } else {
    return "";
  }
};

export const annee = () => {
  var a = new Date().getFullYear();

  var date1 = parseInt(a - 72);
  var date2 = parseInt(a + 1);
  var tab = [];
  for (let dat = date1; date1 < date2; dat++) {
    var data = {
      id: dat.toString(),
      value: dat.toString(),
      label: dat.toString(),
    };
    tab.push(data);
    date1 = date1 + 1;
  }
  return tab.reverse();
};

export const refaCtoNullKey = (value, defaultValue = "") =>
  value &&
  value !== "null" &&
  value !== null &&
  value !== "undefined" &&
  value !== undefined
    ? value
    : defaultValue;

export const uppercaseFirstLetter = (p) =>
  //p?.charAt(0) + p?.slice(1)?.toLowerCase();
  p?.charAt(0).toUpperCase() + p?.slice(1).toLowerCase();

export const fileUrl = (base) => {
  return base && host.baseUrl + "/" + base;
};

export const assetVideoUrl = () =>
  host.baseUrl + "/" + "assets/video/Devinsider_Teaser_Video.mp4";

export const fileUrlDrupal = (base) => baseUrlDrupal + base;

export const linkDeanUrl = (url) => "https://www.linkedin.com/in/" + url;

function replaceDomaine(url) {
  url = url.replace(/(https?:\/\/)?(www.)?(http?:\/\/)?/i, "");
  if (url.indexOf("/") !== -1) {
    return url.split("/")[0];
  }
  return url;
}

export const getDomainWebSite = (url) => {
  if (validConstructorUrl(url)) {
    let domain = new URL(url);
    return domain && domain.hostname.replace("www.", "");
  } else {
    return replaceDomaine(url);
  }
};

export const formatUrlWithHttps = (url) => {
  if (url.startsWith("http://") || url.startsWith("https://")) {
    return url;
  } else {
    return "https://" + url;
  }
};

export const clearSessionObject = () => {
  let completed_account = sessionStorage?.getItem("completed_account") || "";
  sessionStorage.clear();
  sessionStorage.setItem("completed_account", completed_account);
};

export const getSStorage = (p) => {
  let value = sessionStorage?.getItem(p?.toString());
  return value;
};

export const convertUnitFundraising = (prix) => {
  if (parseInt(prix)) {
    let check = prix > 0 && prix < 1000000;
    if (check) {
      return prix / 1000 + "K$";
    } else {
      let tempM = prix / 1000000;
      if (tempM < 60) {
        return tempM + "M$";
      } else {
        return ">50 M$";
      }
    }
  } else {
    return "-";
  }
};

export const checkIfExist = (value) =>
  value &&
  value !== "" &&
  value !== "null" &&
  value !== null &&
  value !== "undefined" &&
  value !== undefined;

export const concatString = (txt, txt2, space = ",") => {
  if (
    txt2 &&
    txt2 !== "null" &&
    txt2 !== null &&
    txt2 !== "undefined" &&
    txt2 !== undefined
  ) {
    return txt + space + " " + txt2;
  } else {
    return txt || "-";
  }
};

export const regexValidation = {
  url: /(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)/g,
};

/**
 * @param state , Array of object with id
 * @param value , items with id
 * @returns Array of response add or remove item if existe
 */
export const customDataStore = (state, value) => {
  let found = state?.find((el) => el?.id == value?.id);
  let returnValue = [];
  if (found) {
    returnValue = [...state?.filter((el) => el.id != value?.id)];
  } else {
    returnValue = [value, ...state];
  }
  return returnValue;
};

export const getBase64 = (file) => {
  return new Promise((resolve) => {
    let fileInfo;
    let baseURL = "";
    // Make new FileReader
    let reader = new FileReader();

    // Convert the file to base64 text
    reader.readAsDataURL(file);

    // on reader load somthing...
    reader.onload = () => {
      // Make a fileInfo Object
      baseURL = reader.result;
      resolve(baseURL);
    };
  });
};

export const checkValidation = (data) => {
  let response = Object.values(data).map((el) => {
    if (typeof el === "string") {
      if (el !== "") {
        return true;
      } else {
        return false;
      }
    } else if (Array.isArray(el)) {
      if (el.length > 0) {
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  });
  return response.every((txt) => txt == true);
};

export const isUrlLinkDean = (linkDean) => {
  if (linkDean && linkDean != "" && linkDean != "null") {
    const haveBaseLnkdean = linkDean?.includes("https://www.linkedin.com/in/");
    const isNotUrlLinkDean = isURL(linkDean) && !haveBaseLnkdean;
    if (isNotUrlLinkDean) {
      return {
        status: false,
      };
    } else {
      let custom = haveBaseLnkdean
        ? linkDean?.replaceAll("https://www.linkedin.com/in/", "")
        : linkDean;
      return { status: true, value: custom };
    }
  } else return { status: true, value: linkDean };
};

export const isUrlOptionnelValue = (url) => {
  if (url) {
    if (url && isURL(url)) {
      return true;
    } else {
      return false;
    }
  } else {
    return true;
  }
};

export const isInObject = (obj, key) => (_isObject(obj) ? key in obj : false);

export const getCountryFromId = (id) => {
  const data = JSON.parse(getSStorage("completed_account") || "{}");
  const countries = data?.country;
  const country = countries?.filter((e) => e.id == id);

  if (country?.length > 0) {
    return country[0];
  }

  return {};
};

export const getFileNameAndExtension = (file) => {
  const name = file.name;
  const lastDot = name.lastIndexOf(".");
  const fileName = name.substring(0, lastDot);
  const ext = name.substring(lastDot + 1);

  return { fileName, ext };
};

export const renderLogo = (url) => {
  return "https://logo.clearbit.com/" + url;
};

/**
 * Remove empty key in draft
 * @param {OemProgramDraft} data
 */
export const handleDataDraftSubmit = (data) => {
  const copyObject = { ...data };
  Object.keys(copyObject).map((e) => {
    const _current = copyObject[e];
    if (!_current) {
      delete copyObject[e];
    }

    if (_isArray(_current) && _current.length < 1) {
      delete copyObject[e];
    }
  });
  return copyObject;
};

export const getExtensionFileName = (filename) => {
  const file = filename.split(".").pop()?.toLowerCase();
  return file === "pdf"
    ? "icon-file-pdf"
    : file?.includes("doc") || file?.includes("docx")
    ? "icon-file-word"
    : "icon-empty-file";
};

export const convertCommentFromJSONToHTML = (text) => {
  let rawContent = convertToRaw(text?.getCurrentContent());
  return draftToHtml(rawContent);
};

export const customClass = (disposition) => {
  return disposition === "textPhoto"
    ? "textFirstimg"
    : disposition === "photoText"
    ? "imgFirsttext"
    : disposition === "upTextPhoto"
    ? "textUpImg"
    : disposition === "upPhotoText"
    ? "imgUpText"
    : "justText";
};

export const isWysiwygEmpty = (data) => {
  if (data?.length == 1) {
    if (data[0].id == "" && data[0].dataFile == "" && data[0].fileUrl == "") {
      return true;
    } else {
      return false;
    }
  }
  return false;
};

export const getMemberDate = () =>
  `Member since ${new Date().toLocaleString("en-US", {
    month: "long",
  })} ${new Date().getDate()}, ${new Date().getFullYear()} `;

export const dateDiff = (dateTime) => {
  let diff = {};
  let tmp = new Date() - new Date(dateTime);

  tmp = Math.floor(tmp / 1000); // Nombre de secondes entre les 2 dates
  diff.sec = tmp % 60; // Extraction du nombre de secondes

  tmp = Math.floor((tmp - diff.sec) / 60); // Nombre de minutes (partie entière)
  diff.min = tmp % 60; // Extraction du nombre de minutes

  tmp = Math.floor((tmp - diff.min) / 60); // Nombre d'heures (entières)
  diff.hour = tmp % 24; // Extraction du nombre d'heures

  tmp = Math.floor((tmp - diff.hour) / 24); // Nombre de jours restants
  diff.day = tmp;

  tmp = Math.floor((tmp - diff.day) / 30);
  diff.mounth = tmp;

  let duration;

  if (diff.mounth != 0) {
    duration = diff.mounth + " mounth ago";
  }
  if (diff.day != 0) {
    duration = diff.day + `${diff.day > 1 ? " days" : " day"} ago`;
  } else if (diff.day == 0 && diff.hour != 0) {
    duration = diff.hour + `${diff.hour > 1 ? " hours" : " hour"} ago`;
  } else if (diff.day == 0 && diff.hour == 0 && diff.min != 0) {
    duration = diff.min + `${diff.min > 1 ? " minutes" : " minute"} ago`;
  } else if (
    diff.day == 0 &&
    diff.hour == 0 &&
    diff.min == 0 &&
    diff.sec != 0
  ) {
    duration = diff.sec < 0 ? "now" : diff.sec + " s";
  }
  return duration;
};

/**
 * @returns data splice data liste in all data
 * @param array
 * @param page_size
 * @param page_number
 */
export const paginate = (array, page_size = 10, page_number = 1) => {
  if (array?.length <= page_size) {
    return array;
  } else {
    return array?.slice((page_number - 1) * page_size, page_number * page_size);
  }
};

export const getUserType = (type) => {
  if (type === ISV) {
    return "isv";
  }
  if (type === INVESTOR) {
    return "investor";
  }

  if (type === RESELLER) {
    return "reseller";
  }

  if (type === OEM_PROGRAM) {
    return "program";
  }

  if (type === GUEST) {
    return "guest";
  }
  if (type === CONTRIBUTOR) {
    return "expert";
  }
  //{/* Dev #47711 V1 [Community] Bug d'affichage sur la page 'Search' lorsque l'on filtre par 'USER' */}
  return "admin";
};

export function createMarkup(html) {
  return {
    __html: html,
  };
}

export const getDescriptionEditorState = (params) => {
  try {
    const parsed = JSON.parse(params);
    if (params) {
      return EditorState?.createWithContent(convertFromRaw(parsed));
    }
    return EditorState.createEmpty();
  } catch (e) {
    return EditorState.createEmpty();
  }
};

export const getPlainText = (params) => {
  try {
    const parsed = JSON.parse(params);
    if (params) {
      return convertFromRaw(parsed).getPlainText();
    }
    return "";
  } catch (e) {
    return "";
  }
};

export const formatHourIsvUpcomingEvent = (hour) => {
  const time = hour.split(" ")[0].split(":");
  const moment = hour.split(" ")[1];

  return `${time[0]}:${time[1]} ${moment}`;
};

export const calculateDate = (current, updated_at) => {
  const end = moment(current);
  const startTime = moment(updated_at);
  const duration = moment.duration(end.diff(startTime));
  if (parseInt(Number(duration.asYears()).toFixed(0)) !== 0) {
    let d = parseInt(Number(duration.asYears()).toFixed(0));
    return d > 1 ? d + " years ago" : d + " year ago";
  }
  if (parseInt(Number(duration.asMonths()).toFixed(0)) !== 0) {
    let d = parseInt(Number(duration.asMonths()).toFixed(0));
    return d > 1 ? d + " months ago" : d + " month ago";
  }
  if (parseInt(Number(duration.asWeeks()).toFixed(0)) !== 0) {
    let d = parseInt(Number(duration.asWeeks()).toFixed(0));
    return d > 1 ? d + " weeks ago" : d + " week ago";
  }
  if (parseInt(Number(duration.asDays()).toFixed(0)) !== 0) {
    let d = parseInt(Number(duration.asDays()).toFixed(0));
    return d > 1 ? d + " days ago" : d + " day ago";
  }
  if (parseInt(Number(duration.asHours()).toFixed(0)) !== 0) {
    let d = parseInt(Number(duration.asHours()).toFixed(0));
    return d > 1 ? d + " hours ago" : d + " hour ago";
  }
  return "minutes ago";
};

export function randomChoice(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

export function generateTextFromEditor(data, limit = null) {
  const textArray = data?.blocks?.map((e) => e.text);
  const text = textArray.join("\n");

  if (limit) {
    return text.substring(0, limit);
  }

  return text;
}

export const extractRoleName = (role) => {
  return role?.[0]?.split("_")[1];
};

export const treatDuration = (duration) => {
  let split = duration?.split(" ");
  split[1] = parseInt(split[0]) > 1 ? split[1] + "s" : split[1];
  return split?.join(" ");
};

export const getAdsEntityTypeByRole = (role) => {
  if (role === INVESTOR) return AdsEntityTypeEnum.INVESTOR_PAGE;
  if (role === ISV) return AdsEntityTypeEnum.COMPANY_PAGE;
  if (role === RESELLER) return AdsEntityTypeEnum.RESELLER_COMPANY;

  return AdsEntityTypeEnum.COMPANY_PAGE;
};

export const addToArray = (array, element) => {
  return !array?.find((el) => el === element)
    ? [...array, element]
    : array?.filter((el) => el !== element);
};

export const extractIdFromUri = (uri) => {
  const splited = uri.split("/");

  return splited[splited?.length - 1];
};

export const postErrorUi = (type, titre = "") => {
  let text = "";
  switch (type) {
    case "community":
      text =
        "You must be affiliated to a company page in order to publish a review about an ISV Partner Program";
      break;
    case "classified-ads":
      text =
        "You must be affiliated to a company page in order to publish classified ads";
      break;
    case "classified-ads-pitch-not-validate":
      text =
        "You must wait for your pitch to be validated by a devinsider admin before you can access this feature";
      break;
    case "classified-investor-page-not-validate":
      text =
        "You must wait for your investor page to be validated by a devinsider admin before you can access this feature";
      break;
    default:
      text = "";
  }

  return confirmAlert({
    customUI: () => {
      return (
        <div className="custom-ui">
          <h1> {titre ? titre : "Complete information"}</h1>
          <p>{text}</p>
        </div>
      );
    },
  });
};

export const changeNumberValue = (value) => {
  if (value < 1000) {
    return value;
  } else if (value < 1000000) {
    return (
      Math.trunc(value / 1000) + "K" + (value / 1000).toString().split(".")[1]
    );
  } else if (value > 1000000) {
    return (
      Math.trunc(value / 1000000) +
      "M" +
      (value / 1000000).toString().split(".")[1]
    );
  }
};

export const generatedText = (currentIndex, allLength) =>
  allLength > 6
    ? currentIndex < 7
      ? `See more(${allLength - currentIndex})`
      : "See less"
    : "";

export const oem_roles = {
  owner: "PROGRAM_OWNER",
  admin: "ADMIN",
  editor: "EDITOR",
  outreach_representative: "OUTREACH_REPRESENTATIVE",
};

export const defaultLabels = [
  { label: "Analytics", value: "Analytics" },
  { label: "Covid-19", value: "Covid-19" },
  { label: "OEM", value: "OEM" },
  { label: "Security", value: "Security" },
  { label: "Software", value: "Software" },
  { label: "Virtualization", value: "Virtualization" },
];

export const conversationType = {
  community: "COMMUNITY",
  isv_to_oem: "ISV_OEM",
  oem_to_isv: "OEM_ISV",
  isv_to_investor: "ISV_INVESTOR",
  investor_to_isv: "INVESTOR_ISV",
  entity_classific_adds: "ENTITY_CLASSIFIED_ADS",
  classific_adds_entity: "CLASSIFIED_ADS_ENTITY",
  all: "ALL",
  MA: "ENTITY_CORPORATE_DEVELOPMENT_M_A",
  MA_entity: "CORPORATE_DEVELOPMENT_M_A_ENTITY",
  alternative: "ALTERNATIVE_FINANCING",
  alternative_ENTITY: "ALTERNATIVE_FINANCING_ENTITY",
};

export const entityName = {
  oem: "Software OEM",
  investor: "Investor",
  isv: "ISV",
};

export const topicTypeStream = {
  newConversation: "CONVERSATION_CONVERSATION",
  newMessage: "CONVERSATION_MESSAGE",
  add_particapant_oem: "CONVERSATION_ADD_MEMBERS",
  new_file_add_in_current_conversation: "CONVERSATION_ADD_SHARED_FILE",
  notifMess: "MESSAGE_HEADER",
};

export const checkIfChowDateMessage = (date1, date2) => {
  let dayOne = new Date(date1 * 1000).getDate();
  let dayTwo = new Date(date2 * 1000).getDate();
  if (dayOne == dayTwo) {
    return true;
  } else if (dayOne != dayTwo || dayTwo == "NAN") {
    return false;
  }
};

export const getAddMember = (allCompanyMember, representatif) => {
  let result = allCompanyMember?.filter((curr) => {
    let isExist = representatif?.find(
      (r) => r?.id?.toString() === curr.user?.id?.toString()
    );
    if (isExist === undefined) {
      return curr;
    }
  });
  return result;
};

const acceptedImageTypes = [
  "gif",
  "jpeg",
  "png",
  "PNG",
  "JPEG",
  "GIF",
  "jif",
  "jpg",
  "jfif",
  "pjp",
  "pjpeg",
  "svg",
];
const acceptedVideoTypes = [
  "webm",
  "webm",
  "flv",
  "mov",
  "mp4",
  "m4p",
  "mpg",
  "mpeg",
  "3gp",
];
export function isFileOrVideo(file, type = "image") {
  if (file) {
    if (type === "image") {
      const extension = file?.split(".")?.[1];
      return extension && acceptedImageTypes.includes(extension);
    } else {
      const extension = file?.split(".")?.[1];
      return extension && acceptedVideoTypes.includes(extension);
    }
  }
  return false;
}

export const isImageExtension = (txt) => {
  const acceptedImageTypes = [
    "gif",
    "jpeg",
    "png",
    "PNG",
    "JPEG",
    "GIF",
    "jif",
    "jpg",
    "jfif",
    "pjp",
    "pjpeg",
    "svg",
  ];
  return acceptedImageTypes.find((el) => el === txt);
};

export const limitedText = (text, limit) => {
  return text && text.length > limit
    ? `${text && text.substring(0, limit)}...`
    : text;
};

export const getTxtNotif = (type) => {
  if (
    type === "REPLY_DISCUSSION_I_PARTICIPATED" ||
    type === "REPLY_DISCUSSION_I_CHECKED_AS_FAVORITE"
  ) {
    return "replied to the forum discussion";
  }
  if (type === "SOLUTION_DISCUSSION_I_PARTICIPATED") {
    return "has received a Best Answer from";
  }
  if (type === "SOLUTION_DISCUSSION_I_CHECKED_AS_FAVORITE") {
    return "has received a Best Answer from";
  }
  if (type === "MESSAGE_I_AM_MENTIONED_IN") {
    return "mentioned you in the forum discussion";
  }
  if (type === "COMMUNITY_MEMBERS_WHO_STARTED_FOLLOWING_ME") {
    return "is now following you.";
  }
  if (type === "NEW_ARTICLE_FROM_COMMUNITY_MEMBER_I_FOLLOW") {
    return "posted a New Article";
  }
  if (type === "NEW_DISCUSSION_FROM_COMMUNITY_MEMBER_I_FOLLOW" ) {
    return "posted a New Discussion";
  }
  if (type === "NEW_ARTICLE_FOR_CATEGORY_I_FOLLOW") {
    return "posted a New Article";
  }
  if (type === "NEW_DISCUSSION_FOR_CATEGORY_I_FOLLOW") {
    return "posted a New Discussion";
  }
  if (type === "REPLY_DISCUSSION_I_CREATED") return "replied to your post";
};

export const renderCompanyTexte = (role) => {
  if (role === ISV) return " company page";
  if (role === INVESTOR) return " investor page";
  if (role === OEM_PROGRAM) return " program page";
  return "company";
};

export const checkType = (role, typeSelected = []) => {
  const type = role.filter((el) => el !== "USER_ROLE")[0];
  const foud = typeSelected.find((el) => el === type);
  return foud ? true : false;
};

export const getUserContected = (role, typeSelected) => {
  const type = role.filter((el) => el !== "USER_ROLE")[0];
  return typeSelected === type ? true : false;
};

export const getTxtNameAccount = (role) => {
  const type = role.filter((el) => el !== "USER_ROLE")[0];

  if (type === ISV) {
    return "Isv";
  }

  if (type === RESELLER) {
    return "Reseller";
  }

  if (type === OEM_PROGRAM || type === INVESTOR) {
    return "User Profile";
  }

  if (type === GUEST) {
    return "Guest";
  }
  if (type === CONTRIBUTOR) {
    return "Expert Contributor";
  }
};

export const getMonth = (month) => {
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  return months[month];
};

export const mounths = [
  {
    id: 0,
    label: "01-01",
    value: "January",
  },
  {
    id: 1,
    label: "02-01",
    value: "February",
  },
  {
    id: 2,
    label: "03-01",
    value: "March",
  },
  {
    id: 3,
    label: "04-01",
    value: "April",
  },
  {
    id: 4,
    label: "05-01",
    value: "May",
  },
  {
    id: 5,
    label: "06-01",
    value: "June",
  },
  {
    id: 6,
    label: "07-01",
    value: "July",
  },
  {
    id: 7,
    label: "08-01",
    value: "August",
  },

  {
    id: 8,
    label: "09-01",
    value: "September",
  },
  {
    id: 9,
    label: "10-01",
    value: "October",
  },
  {
    id: 10,
    label: "11-01",
    value: "November",
  },
  {
    id: 11,
    label: "12-01",
    value: "December",
  },
];

export const makeClass = (allSelected, allNotSelected, el, useSelected) => {
  let className;
  if (useSelected) {
    className = allSelected?.find(
      (allSelected) => allSelected?.toString() === el?.toString()
    )
      ? "active"
      : "";
    return className;
  } else {
    className = allNotSelected?.find(
      (notSelectedLabel) => notSelectedLabel?.toString() === el?.toString()
    )
      ? ""
      : "active";
    return className;
  }
};

export const isModerator = (roles) => roles?.[0] === MODERATOR;

export const checkIsCompletedOemCompanyMatching = (companyPageObject) => {
  let isCompletedInfoCompany = validateCompleteInfo(companyPageObject);
  let isCompletedSummary = isRequired(companyPageObject?.company_summary);
  let isCompletedLogo = isHaveCompanyLogo(companyPageObject.photo_profil);
  let isCompletedTargetedAndTypeOffice = isRequired(
    companyPageObject?.targeted_country
  );
  let isCompletedPartenerAndProgramInterested = isRequired(
    companyPageObject?.oem_program_segment_interest
  );
  let isCompletedAnnualTurnOver = isRequired(
    companyPageObject?.annual_turnover
  );
  let isCompletedTargetedIndustry = isRequired(
    companyPageObject?.targeted_industry
  );
  let isCompletedDistributionAndLicencing =
    isRequired(companyPageObject?.licensing_model) &&
    isRequired(companyPageObject?.distribution_channel);

  let isCompletedWebSite = isRequired(companyPageObject?.website);

  return (
    // isCompletedInfoCompany &&
    isCompletedSummary &&
    isCompletedLogo &&
    isCompletedTargetedAndTypeOffice &&
    isCompletedPartenerAndProgramInterested &&
    isCompletedAnnualTurnOver &&
    isCompletedTargetedIndustry &&
    isCompletedDistributionAndLicencing &&
    isCompletedWebSite
  );
};

export const checkIsCompletedInvestorCompanyMatching = (companyPageObject) => {
  let isCompletedInfoCompany = validateCompleteInfo(companyPageObject);
  let isCompletedSummary = isRequired(companyPageObject?.company_summary);
  let isCompletedLogo = isHaveCompanyLogo(companyPageObject.photo_profil);
  let isCompletedDistributionAndLicencing =
    isRequired(companyPageObject?.licensing_model) &&
    isRequired(companyPageObject?.distribution_channel);
  let isCompletedPartenerAndProgramInterested = isRequired(
    companyPageObject?.oem_program_segment_interest
  );
  let isCompletedAnnualTurnOver = isRequired(
    companyPageObject?.annual_turnover
  );
  let isCompletedFundingRound = isRequired(companyPageObject?.funding_round);
  let isCompletedProductDevelopment = isRequired(
    companyPageObject?.future_milestones_product_development
  );
  let isCompletedAmountToRise = isRequired(
    companyPageObject?.amount_want_to_raise
  );
  let isCompletedPaintPointPitch = isRequired(
    companyPageObject?.paint_point_pitch
  );

  let isCompletedTeamUnique = isRequired(
    companyPageObject?.team_differentiator_pitch
  );
  let isCompletedKeyDifferent = isRequired(
    companyPageObject?.key_competitive_differentiator_pitch
  );

  let isCompletedTargetedIndustry = isRequired(
    companyPageObject?.targeted_industry
  );

  let isCompletedTargetedAndTypeOffice = isRequired(
    companyPageObject?.targeted_country
  );

  let isCompletedExpressionStrategy = isRequired(
    companyPageObject?.futur_milestones_expansion_strategy
  );

  return (
    isCompletedInfoCompany &&
    isCompletedExpressionStrategy &&
    isCompletedTargetedIndustry &&
    isCompletedTargetedAndTypeOffice &&
    isCompletedKeyDifferent &&
    isCompletedPaintPointPitch &&
    isCompletedAmountToRise &&
    isCompletedProductDevelopment &&
    isCompletedFundingRound &&
    isCompletedAnnualTurnOver &&
    isCompletedPartenerAndProgramInterested &&
    isCompletedDistributionAndLicencing &&
    isCompletedLogo &&
    isCompletedSummary &&
    isCompletedTeamUnique
  );
};

export const bolderedText = (txt) => {
  return (
    txt &&
    txt.replaceAll(HIDDEN_INFORMATION, "<b>" + HIDDEN_INFORMATION + "</b>")
  );
};

export const toCapitalize = (s) => {
  if (typeof s !== "string") return "";
  return s.charAt(0)?.toUpperCase() + s?.slice(1)?.toLocaleLowerCase();
};

export const checkIsProfilCompleted = (userObject) => {
  let isCompletedFirstName = isRequired(userObject?.first_name);

  let isCompletedLastName = isRequired(userObject?.last_name);

  let isCompletedCountry = isRequired(
    userObject?.country && userObject?.country.country_name
  );

  let isCompletedCompanyName = isRequired(userObject?.company_name);

  let isCompletedJobTitle = isRequired(userObject?.job_title);

  return (
    isCompletedFirstName &&
    isCompletedLastName &&
    isCompletedCountry &&
    isCompletedCompanyName &&
    isCompletedJobTitle
  );
};

export const isAvatarPhoto = (fileName) => {
  const file = fileName && fileName.split("/");
  return file[file.length - 1] == "avatar.png" ? true : false;
};

export const formatAMPM = (date) => {
  var hours = date.getHours();
  var ampm = hours >= 12 ? "pm" : "am";
  return ampm;
};

export const checkSpacingTxtPhoneNumber = (selected) => {
  return contact?.find((el) => el.code == selected)?.dial_code?.length === 2
    ? 35
    : contact?.find((el) => el.code == selected)?.dial_code?.length === 3
    ? 40
    : contact?.find((el) => el.code == selected)?.dial_code?.length === 4
    ? 50
    : 60;
};

export const isExist = (value) =>
  value &&
  value !== "false" &&
  value !== 0 &&
  value !== "0" &&
  value !== "null" &&
  value !== null &&
  value !== "undefined" &&
  value !== undefined;

export const setInternationalPhoneNumber = (phoneNumber, dialCode) => {
  phoneNumber = refaCtoNullKey(phoneNumber);
  if (phoneNumber && phoneNumber != "-") {
    if (phoneNumber.charAt(0) === "0") {
      phoneNumber = phoneNumber.substring(1);
    }
    return dialCode ? dialCode + phoneNumber : phoneNumber;
  }
  return phoneNumber;
};

export const handleStatus = (type) => {
  let statusValue = "";
  switch (type) {
    case "PENDING_DISCUSSION":
      statusValue = "Pending Discussion";
      break;
    case "IN_DISCUSSION":
      statusValue = "In Discussion";
      break;
    case "ARCHIVED_DISCUSSION":
      statusValue = "Archived Discussion";
      break;
    case "NOT_CONTACTED":
      statusValue = "Not Contacted";
      break;

    default:
      statusValue = "";
      break;
  }
  return statusValue;
};

export const handleClassName = (type) => {
  let classValue = "";
  switch (type) {
    case "PENDING_DISCUSSION":
      classValue = "hold";
      break;
    case "IN_DISCUSSION":
      classValue = "connected";
      break;
    case "ARCHIVED_DISCUSSION":
      classValue = "archived";
      break;
    case "NOT_CONTACTED":
      classValue = "notConnected";
      break;

    default:
      classValue = "";
      break;
  }
  return classValue;
};

export const exportedCompanySize = (min, max) => {
  let tempMin = 0;
  let tempMax = 0;
  switch (min) {
    case 0:
      tempMin = 0;
      break;
    case 1:
      tempMin = 5;
      break;
    case 2:
      tempMin = 15;
      break;
    case 3:
      tempMin = 25;
      break;
    case 4:
      tempMin = 50;
      break;
    case 5:
      tempMin = 100;
      break;
    case 6:
      tempMin = 250;
      break;
    case 7:
      tempMin = 500;
      break;
    case 8:
      tempMin = 1000;
      break;
    case 9:
      tempMin = 3000;
      break;
    case 10:
      tempMin = 3000;
      break;
    default:
      break;
  }
  switch (max) {
    case 0:
      tempMax = 0;
      break;
    case 1:
      tempMax = 5;
      break;
    case 2:
      tempMax = 15;
      break;
    case 3:
      tempMax = 25;
      break;
    case 4:
      tempMax = 50;
      break;
    case 5:
      tempMax = 100;
      break;
    case 6:
      tempMax = 250;
      break;
    case 7:
      tempMax = 500;
      break;
    case 8:
      tempMax = 1000;
      break;
    case 9:
      tempMax = 3000;
      break;
    case 10:
      tempMax = 3200;
      break;
    default:
      break;
  }

  return {
    tempMin,
    tempMax,
  };
};

export const urlCommunity = [
  "/community/preview",
  "/community/create-discussion",
  "/community/create-article",
  "/community/post/press-release",
  "/community/post/program-review",
];

export const getLooUpKeyPostMA = (role) => {
  return role === ISV
    ? "CORPO_DEV_ISV"
    : role === GUEST
    ? "CORPO_DEV_GUEST"
    : role === RESELLER
    ? "CORPO_DEV_RESELLER"
    : role === OEM_PROGRAM
    ? "CORPO_DEV_OEM"
    : role === INVESTOR
    ? "CORPO_DEV_INVESTOR"
    : role === "ROLE_MODERATOR"
    ? 'CORPO_DEV_MODERATOR'
    : "";
};

export const getLooUpKeyPostEVENT = (role) => {
  return role === ISV
    ? "ISV_POST_EVENT"
    : role === CONTRIBUTOR
    ? "EXPERT_POST_EVENT"
    : role === RESELLER
    ? "RESELLER_POST_EVENT"
    : role === OEM_PROGRAM
    ? "OEM_POST_EVENT"
    : role === INVESTOR
    ? "INVESTOR_POST_EVENT"
    : "";
};

export const handleDateEvent = (date) => {
  if (date?.toString() === "0.5 h") {
    return " 30 min";
  }
  return date?.toString() === "24 h" ? "All day event" : date;
};

export const concatStringArray = (str) => {
  var rolesString = "('" + str.join("','") + "')";
  return rolesString;
};

export const makeOptions = (data, value = "id", label = "name") =>
  data &&
  data.map((el) => ({
    ...el,
    label: el[label],
    value: el[value],
  }));

export const getTimeZone = (utc) => {
  const gmt = timeZone?.find((el) => el.utc === utc);
  if (gmt) {
    return `GMT ` + gmt.offset;
  }
  return "";
};

export const handleEventType = (ev) => {
  const countryList =
    JSON.parse(sessionStorage.getItem("completed_account") || "{}") || [];
  const isPhysicalEvent = ev.event_type?.id?.toString() === "2";
  if (isPhysicalEvent) {
    if (ev.country_id) {
      const country = countryList?.country?.find(
        (c) => c.id?.toString() === ev.country_id?.toString()
      );
      if (country) {
        return country.country_name + " " + ev.city;
      }
    }
    return "Physical Event";
  }
  return ev.event_type.name;
};

export const shuffle = (array) => {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
};

export const getTimeZoneUtc = () => {
  return momentTimezone.tz.guess();
}

export const getGMTOffset = (timezone) => {
  const offsetMinutes = momentTimezone.tz(timezone).utcOffset();
  const sign = offsetMinutes >= 0 ? '' : '-';
  const hours = Math.floor(Math.abs(offsetMinutes) / 60);
  const minutes = Math.abs(offsetMinutes) % 60;

  return minutes === 0
    ? `GMT ${sign}${String(hours).padStart(2)}`
    : `GMT ${sign}${String(hours).padStart(2)}.${String(minutes).padStart(2)}`;
};
export const saveToLocalStorage = (key, data) => {
  try {
    const serializedData = JSON.stringify(data);
    localStorage.setItem(key, serializedData);
  } catch (error) {
    console.error("Erreur lors de la sauvegarde dans localStorage:", error);
  }
};

// Fonction pour récupérer un objet depuis localStorage
export const getFromLocalStorage = (key) => {
  try {
    const serializedData = localStorage.getItem(key);
    return serializedData ? JSON.parse(serializedData) : null;
  } catch (error) {
    console.error("Erreur lors de la récupération depuis localStorage:", error);
    return null;
  }
};
